import { PkCarousel } from '@uefadigital/panenka-react';
import { memo, useMemo } from 'react';

import EventPlayer from '../../../../components/liveblog/EventPlayer';
import TeamFlag from '../../../MatchUnit/utils/TeamFlag';
import { useTranslateFn } from '../../../Translate';

import { hydrateWithTranslations } from '@services/TranslatorService';
import type { PlayerEvent, Match } from '@shared-types/match.types';

import * as styles from './styles.module.scss';

const getMinuteLabel = (ev: PlayerEvent) => {
  let minutes = `${ev.time?.minute}'`;
  if (ev.time?.injuryMinute) minutes += `+${ev.time.injuryMinute}`;

  return minutes;
};

export const SubstitutionCarousel = ({
  substitutions,
  enableLink,
  match,
}: {
  substitutions: PlayerEvent[];
  enableLink: boolean;
  match: Match;
}) => {
  const t = useTranslateFn();

  const orderedSubs = useMemo(
    () =>
      substitutions?.toSorted(
        (a, b) =>
          a.time?.minute - b.time?.minute ||
          a.time?.second - b.time?.second ||
          a.time?.injuryMinute - b.time?.injuryMinute,
      ),
    [substitutions],
  );
  if (!orderedSubs?.length) return null;

  return (
    <div className='card-match card-substitutions pk-theme--light'>
      <PkCarousel
        key={orderedSubs.map((s) => s.id).join('-')}
        slidesPerView={1.1}
        breakpoints={JSON.stringify({
          0: {
            slidesPerView: 1.1,
            spaceBetween: 8,
          },
        })}
        boxShadowPadding
        className={styles.slider}
      >
        <h3 slot='headline' className={styles.title}>
          {t('substitutions')}
        </h3>
        {orderedSubs.map((s) => (
          <div key={s.id} className={styles.card}>
            <div className={styles.header}>
              <div className={styles.minute}>{getMinuteLabel(s)}</div>
              <TeamFlag
                className={styles.team}
                team={s.primaryActor.team}
                teamLink={enableLink}
                phase={match?.round?.phase}
              />
            </div>
            {s.primaryActor.person && (
              <EventPlayer
                className={styles.player}
                id={s.primaryActor.person.id}
                name={hydrateWithTranslations(s.primaryActor.person).name}
                fieldPosition={hydrateWithTranslations(s.primaryActor.person).fieldPosition}
                imageUrl={s.primaryActor.person.imageUrl}
                substitutionStatus='out'
                enableLink={enableLink}
                phase={match?.round?.phase}
              />
            )}
            {s.secondaryActor.person && (
              <EventPlayer
                className={styles.player}
                id={s.secondaryActor.person.id}
                name={hydrateWithTranslations(s.secondaryActor.person).name}
                fieldPosition={hydrateWithTranslations(s.secondaryActor.person).fieldPosition}
                imageUrl={s.secondaryActor.person.imageUrl}
                substitutionStatus='in'
                enableLink={enableLink}
                phase={match?.round?.phase}
              />
            )}
          </div>
        ))}
      </PkCarousel>
    </div>
  );
};

export default memo(SubstitutionCarousel);
